/**
 * The stat rows an item card shows for a piece of equipment, compared with
 * the equipment it would replace.
 */
import { CORE_STAT_LABELS, ITEM_RARITY_NAMES, type Equipment, type EquipmentBonuses, type EquipmentCombatStats, type GearDiscipline, type UpgradeStat } from '@sorrem/shared';
import type { UIInventoryItem } from '@sorrem-client/types';

/** The rows an item's own combat values show in. */
type CombatStatKey = 'damage' | 'attackSpeed' | 'reach' | 'accuracy' | 'defense' | 'combatResistance' | 'speedBonus';

/** Every row an item card can show: its combat values, then each bonus to the wearer. */
export type ItemCardStatKey = CombatStatKey | keyof EquipmentBonuses;

export interface ItemCardStat {
    readonly key: ItemCardStatKey;
    readonly label: string;
    /** Formatted value of the item's stat. */
    readonly value: string;
    /** How the stat compares with each compared item, in order (#341): none with nothing to compare against. */
    readonly deltas: readonly ItemCardDelta[];
    /** Whether the stat is a bonus added to the wearer's own (Speed, Dodge...), shown signed, rather than the item's own combat value. */
    readonly isBonus: boolean;
}

/** A stat's change against one compared item. */
export interface ItemCardDelta {
    /** Positive is better. */
    readonly delta: number;
    /** Formatted, e.g. `+3` or `-0.10`; empty when even. */
    readonly text: string;
}

type StatSource = Pick<UIInventoryItem, 'bonuses' | 'combat'>;

interface StatDefinition {
    readonly key: ItemCardStatKey;
    readonly label: string;
    readonly read: (source: StatSource) => number | undefined;
    readonly decimals: number;
    /** Read from `bonuses`: added to the wearer's own stat. */
    readonly isBonus?: boolean;
}

function combatStat(key: keyof EquipmentCombatStats): (source: StatSource) => number | undefined {
    return (source) => {
        const value = source.combat?.[key];
        return typeof value === 'number' ? value : undefined;
    };
}

function bonusStat(key: keyof EquipmentBonuses): (source: StatSource) => number | undefined {
    return (source) => source.bonuses?.[key];
}

/** Combat rows in display order. Attack speed is shown as attacks per second, so higher is better like every other row. */
const COMBAT_STAT_DEFINITIONS: ReadonlyArray<StatDefinition & { readonly key: CombatStatKey }> = [
    { key: 'damage', label: 'Damage', read: combatStat('baseDamage'), decimals: 0 },
    {
        key: 'attackSpeed',
        label: 'Attack Speed',
        read: (source): number | undefined => {
            const cooldownMs = source.combat?.attackCooldownMs;
            return cooldownMs !== undefined && cooldownMs > 0 ? 1000 / cooldownMs : undefined;
        },
        decimals: 2,
    },
    { key: 'reach', label: 'Reach', read: combatStat('range'), decimals: 1 },
    { key: 'accuracy', label: 'Accuracy', read: combatStat('accuracy'), decimals: 0 },
    { key: 'defense', label: 'Defense', read: combatStat('defense'), decimals: 0 },
    { key: 'combatResistance', label: 'Resistance', read: combatStat('resistance'), decimals: 0 },
    { key: 'speedBonus', label: 'Attack Speed Bonus', read: combatStat('speedBonus'), decimals: 0 },
];

/** Bonus rows in display order: every bonus an item can carry must have one, or it would never show. */
const BONUS_STAT_ROWS: Readonly<Record<keyof Required<EquipmentBonuses>, { readonly label: string; readonly decimals: number }>> = {
    melee: { label: CORE_STAT_LABELS.melee, decimals: 0 },
    range: { label: CORE_STAT_LABELS.range, decimals: 0 },
    conviction: { label: CORE_STAT_LABELS.conviction, decimals: 0 },
    dodge: { label: CORE_STAT_LABELS.dodge, decimals: 0 },
    speed: { label: CORE_STAT_LABELS.speed, decimals: 0 },
    resistance: { label: CORE_STAT_LABELS.resistance, decimals: 0 },
    intellect: { label: 'Intellect', decimals: 0 },
    essencePool: { label: 'Essence', decimals: 0 },
    loadoutSlots: { label: 'Carried Slots', decimals: 0 },
};

/** Every row in display order. */
const STAT_DEFINITIONS: readonly StatDefinition[] = [
    ...COMBAT_STAT_DEFINITIONS,
    ...(Object.keys(BONUS_STAT_ROWS) as Array<keyof EquipmentBonuses>).map((key): StatDefinition => ({
        key,
        ...BONUS_STAT_ROWS[key],
        read: bonusStat(key),
        isBonus: true,
    })),
];

/** The card row each upgrade stat shows in. */
export const UPGRADE_STAT_CARD_KEYS: Readonly<Record<UpgradeStat, ItemCardStatKey>> = {
    damage: 'damage',
    accuracy: 'accuracy',
    resistance: 'resistance',
    defense: 'defense',
    essencePool: 'essencePool',
};

function formatNumber(value: number, decimals: number): string {
    return value.toFixed(decimals);
}

function formatDelta(delta: number, decimals: number): string {
    const text = formatNumber(Math.abs(delta), decimals);
    return delta > 0 ? `+${text}` : `-${text}`;
}

/**
 * The item's stats, each compared with every item in `compared` (the
 * equipment it would swap out; both hands' for a one-handed weapon when both
 * hold something, #341). A stat only a compared item has shows as 0 and a
 * loss. With nothing compared, there are no deltas. Stats keyed in
 * `alwaysShow` show even when the item lacks them, as 0 (e.g. what an
 * upgrade raises, #287).
 */
export function buildItemCardStats(item: StatSource, compared: ReadonlyArray<Pick<Equipment, 'bonuses' | 'combat'>>, alwaysShow: readonly ItemCardStatKey[] = []): ItemCardStat[] {
    return STAT_DEFINITIONS.flatMap((definition): ItemCardStat[] => {
        const value = definition.read(item);
        const comparedValues = compared.map((entry) => definition.read(entry));
        if (value === undefined && comparedValues.every((entry) => entry === undefined) && !alwaysShow.includes(definition.key)) {
            return [];
        }

        const own = value ?? 0;
        const rounded = (amount: number): number => Number(formatNumber(amount, definition.decimals));
        return [{
            key: definition.key,
            label: definition.label,
            value: formatNumber(own, definition.decimals),
            deltas: comparedValues.map((comparedValue): ItemCardDelta => {
                const delta = rounded(own) - rounded(comparedValue ?? 0);
                return { delta, text: delta === 0 ? '' : formatDelta(delta, definition.decimals) };
            }),
            isBonus: definition.isBonus ?? false,
        }];
    });
}

const EQUIPMENT_TYPE_LABELS: Record<NonNullable<UIInventoryItem['equipmentType']>, string> = {
    melee: 'Melee weapon',
    range: 'Ranged weapon',
    shield: 'Shield',
    armor: 'Armor',
    ring: 'Ring',
    amulet: 'Amulet',
};

/** What kind of equipment an item is, e.g. `Melee weapon`; empty for anything else. */
export function formatEquipmentTypeLabel(equipmentType: UIInventoryItem['equipmentType']): string {
    return equipmentType === undefined ? '' : EQUIPMENT_TYPE_LABELS[equipmentType];
}

/** An item's kind, with its rarity and item level when it has rolls, e.g. "Rare melee weapon - Level 4". */
export function formatItemKind(item: Pick<UIInventoryItem, 'equipmentType' | 'instance'>): string {
    const kind = formatEquipmentTypeLabel(item.equipmentType);
    if (!item.instance) {
        return kind;
    }
    const rarity = ITEM_RARITY_NAMES[item.instance.rarity];
    return `${rarity} ${kind.toLowerCase()} - Level ${item.instance.itemLevel}`;
}

/** The disciplines gear answers to, as players know them. */
const GEAR_DISCIPLINE_NAMES: Record<GearDiscipline, string> = {
    'veil-attunement': 'Veil Attunement',
    'martial-discipline': 'Martial Discipline',
};

/** An item's requirement line (#250), e.g. `Requires 30% Veil Attunement`. */
export function formatGearRequirement(requirement: NonNullable<UIInventoryItem['requirement']>): string {
    return `Requires ${requirement.percent}% ${GEAR_DISCIPLINE_NAMES[requirement.discipline]}`;
}

/** What a weakened item still gives (#250), e.g. `Weakened: you get 50% of its bonuses`. */
export function formatGearWeakening(potency: number): string {
    return `Weakened: you get ${Math.round(potency * 100)}% of its bonuses`;
}
